import React, { useState, useEffect } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import axios from "axios";
import { API_BASE } from "../config";

export default function CustomProgramNotSubmited() {
  const [energyData, setEnergyData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [activities, setActivities] = useState([]);

  // UI selected filters
  const [selectedDistrict, setSelectedDistrict] = useState("0");
  const [selectedActivity, setSelectedActivity] = useState("0");

  const getCurrentFY = () => {
    const t = new Date();
    const y = t.getFullYear();
    const m = t.getMonth() + 1;
    return m < 4 ? `${y - 1}-${y}` : `${y}-${y + 1}`;
  };

  const currentFY = getCurrentFY();
  const [selectedFY, setSelectedFY] = useState(currentFY);

  const getFYList = () => {
    const [s, e] = currentFY.split("-").map(Number);
    return [`${s - 1}-${e - 1}`, `${s}-${e}`, `${s + 1}-${e + 1}`];
  };

  const financialYears = getFYList();

  // Applied filters used for charts
  const [appliedDistrict, setAppliedDistrict] = useState("0");
  const [appliedActivity, setAppliedActivity] = useState("0");
  const [appliedFY, setAppliedFY] = useState(currentFY);

  const getFYDates = (fy) => {
    const [s, e] = fy.split("-");
    return { fy_start: `${s}-04-01`, fy_end: `${e}-03-31` };
  };

  // Load master data once
  useEffect(() => {
    axios.get(`${API_BASE}/districts`).then((r) => setDistricts(r.data)).catch(()=>{});
    axios.get(`${API_BASE}/activities`).then((r) => setActivities(r.data)).catch(()=>{});
  }, []);

  // Fetch not-submission summary
  const fetchEnergyClub = async (dist = appliedDistrict, activity = appliedActivity, fy = appliedFY) => {
    try {
      const { fy_start, fy_end } = getFYDates(fy);
      const res = await axios.get(`${API_BASE}/custom-notsubmission-summary`, {
        params: { dist, activity, fy_start, fy_end },
      });

      const barFormatted = res.data.map((row) => ({
        name: row.district_name,
        district_id: row.dist_id,
        count: Number(row.total_submissions),
      }));

      const pieFormatted = res.data.map((row) => ({
        id: row.dist_id,
        label: row.district_name,
        district_id: row.dist_id,
        value: Number(row.total_submissions),
      }));

      setEnergyData(barFormatted);
      setPieData(pieFormatted);
    } catch (err) {
      console.error("Not submitted fetch error:", err);
      setEnergyData([]);
      setPieData([]);
    }
  };

  useEffect(() => {
    // initial load
    fetchEnergyClub();
  }, []);

  // Dynamic heading names
  const appliedDistrictName =
    appliedDistrict === "0"
      ? "All"
      : districts.find((d) => String(d.dist_id) === appliedDistrict)?.district || "All";

  const appliedActivityName =
    appliedActivity === "0"
      ? "All"
      : activities.find((a) => String(a.activities_id) === appliedActivity)?.activity || "All";

  const appliedFYLabel = appliedFY;

  // Chart settings
  const barChartSettings = {
    keys: ["count"],
    indexBy: "name",
    margin: { top: 80, right: 50, bottom: 160, left: 70 },
    padding: 0.35,
    borderRadius: 6,
    colors: (bar) => ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0"][bar.index % 4],
    axisBottom: {
      tickRotation: -35,
      tickPadding: 15,
      legend: "Districts",
      legendOffset: 100,
      legendPosition: "middle",
    },
    axisLeft: {
      legend: "Not Submitted Count",
      legendOffset: -50,
      legendPosition: "middle",
    },
  };

  const pieChartSettings = {
    margin: { top: 40, right: 20, bottom: 40, left: 20 },
    innerRadius: 0.5,
    padAngle: 0.7,
    cornerRadius: 4,
    activeOuterRadiusOffset: 6,
    colors: { scheme: "pastel1" },
  };

  const chartHeading = (label) => (
    <span>
      {label} (
      District: <span style={{ color: "green", fontWeight: 600 }}>{appliedDistrictName}</span> |
      Activity: <span style={{ color: "green", fontWeight: 600 }}>{appliedActivityName}</span> |
      FY: <span style={{ color: "green", fontWeight: 600 }}>{appliedFYLabel}</span>)
    </span>
  );

  return (
    <div style={{ padding: 30, background: "#f4f6f8", minHeight: "100vh" }}>
      <h2 style={{ marginTop: 0 }}>Custom Programme Not Submitted</h2>

      {/* FILTERS */}
      <div style={{ display: "flex", gap: 20, marginBottom: 20 }}>
        <label>
          District:
          <select value={selectedDistrict} onChange={(e) => setSelectedDistrict(e.target.value)} style={{ marginLeft: 10, padding: 5 }}>
            <option value="0">All</option>
            {districts.map((d) => (
              <option key={d.dist_id} value={d.dist_id}>{d.district}</option>
            ))}
          </select>
        </label>

        <label>
          Activity:
          <select value={selectedActivity} onChange={(e) => setSelectedActivity(e.target.value)} style={{ marginLeft: 10, padding: 5 }}>
            <option value="0">All</option>
            {activities.map((a) => (
              <option key={a.activities_id} value={a.activities_id}>{a.activity}</option>
            ))}
          </select>
        </label>

        <label>
          Financial Year:
          <select value={selectedFY} onChange={(e) => setSelectedFY(e.target.value)} style={{ marginLeft: 10, padding: 5 }}>
            {financialYears.map((fy) => (
              <option key={fy} value={fy}>{fy}</option>
            ))}
          </select>
        </label>

        <button
          onClick={() => {
            // apply filters and fetch
            setAppliedDistrict(selectedDistrict);
            setAppliedActivity(selectedActivity);
            setAppliedFY(selectedFY);
            fetchEnergyClub(selectedDistrict, selectedActivity, selectedFY);
          }}
          style={{ padding: "10px 20px", background: "#2196F3", color: "#fff", borderRadius: 6, cursor: "pointer" }}
        >
          Search
        </button>
      </div>

      {/* BAR CHART */}
      <div style={{ background: "#fff", padding: 20, borderRadius: 12, height: 450, marginBottom: 30 }}>
        <h3>{chartHeading("Bar Chart")}</h3>

        <ResponsiveBar
          data={energyData}
          {...barChartSettings}
          isInteractive={true}
          onMouseEnter={() => { document.body.style.cursor = "pointer"; }}
          onMouseLeave={() => { document.body.style.cursor = "default"; }}
          tooltip={({ data }) => (
            <div style={{ padding: "6px 10px", background: "#fff", borderRadius: 6, boxShadow: "0 2px 6px rgba(0,0,0,0.2)" }}>
              <strong>{data.name}</strong>: {data.count}
            </div>
          )}
          onClick={(bar) => {
            localStorage.setItem("viewFromChart", "1");
            localStorage.setItem("viewCustomFilters", JSON.stringify({
              district: String(bar.data.district_id),
              fy: appliedFY,
              activity: appliedActivity,
              status: "not_submitted",
            }));
            window.open("/View_not_custom_list", "_blank");
          }}
        />
      </div>

      {/* PIE CHART */}
      <div style={{ background: "#fff", padding: 20, borderRadius: 12, height: 450 }}>
        <h3>{chartHeading("Pie Chart")}</h3>

        <ResponsivePie
          data={pieData}
          {...pieChartSettings}
          isInteractive={true}
          onMouseEnter={() => { document.body.style.cursor = "pointer"; }}
          onMouseLeave={() => { document.body.style.cursor = "default"; }}
          tooltip={({ datum }) => (
            <div style={{ padding: "6px 10px", background: "#fff", borderRadius: "6px", boxShadow: "0 2px 6px rgba(0,0,0,0.2)" }}>
              <strong>{datum.label}</strong>: {datum.value}
            </div>
          )}
          arcLinkLabel={(d) => d.label}
          onClick={(slice) => {
            localStorage.setItem("viewFromChart", "1");
            localStorage.setItem("viewCustomFilters", JSON.stringify({
              district: String(slice.data.district_id),
              fy: appliedFY,
              activity: appliedActivity,
              status: "not_submitted",
            }));
            window.open("/View_not_custom_list", "_blank");
          }}
        />
      </div>
    </div>
  );
}